/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { inject, observer } from 'mobx-react';
import { decorate, computed, observable, action, runInAction } from 'mobx';
import { Header, Modal, Segment, Icon, Checkbox } from 'semantic-ui-react';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';
import Dropdown from '@amzn/base-ui/dist/parts/helpers/fields/DropDown';
import Form from '@amzn/base-ui/dist/parts/helpers/fields/Form';
import Input from '@amzn/base-ui/dist/parts/helpers/fields/Input';
import TextArea from '@amzn/base-ui/dist/parts/helpers/fields/TextArea';
import YesNo from '@amzn/base-ui/dist/parts/helpers/fields/YesNo';

import { getCreateStudyForm } from '../../models/forms/CreateStudy';
import { getCategoryById } from '../../models/studies/categories';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - userStore (via injection)
// - studiesStoresMap (via injection)
class CreateStudy extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.cleanModal();
      this.form = getCreateStudyForm();
    });
  }

  getStudiesStore(categoryId) {
    return this.props.studiesStoresMap[categoryId];
  }

  get isAdmin() {
    return this.props.userStore.user.isAdmin;
  }

  get showAsResearcher() {
    return this.isAdmin && this.props.userStore.researcherView;
  }

  cleanModal = () => {
    runInAction(() => {
      this.modalOpen = false;
    });
  };

  handleFormCancel = form => {
    form.clear();
    this.cleanModal();
  };

  handleFormError = (_form, _errors) => {};

  handleFormSubmission = async form => {
    try {
      const studyValues = form.values();
      const categoryId = studyValues.categoryId;
      const categoryName = (getCategoryById(categoryId) || {}).name;
      const studiesStore = this.getStudiesStore(categoryId);

      delete studyValues.categoryId;

      // Only pass isLicense for Organization studies
      if (categoryId !== 'organization') {
        delete studyValues.isLicense;
      }

      await studiesStore.createStudy({ ...studyValues, category: categoryName });
      form.clear();
      this.cleanModal();
    } catch (error) {
      displayError(error);
    }
  };

  render() {
    // Don't show the button to non-admins or when in researcher view mode
    if (!this.isAdmin || this.showAsResearcher) {
      return null;
    }

    return (
      <Modal
        closeIcon
        trigger={this.renderTrigger()}
        open={this.modalOpen}
        onClose={this.cleanModal}
        className="create-study-modal"
      >
        <div className="mt2">
          <Header as="h3" icon textAlign="center" className="mt3" color="grey">
            Create Study
          </Header>
          <div className="mx3">{this.renderCreateStudyForm()}</div>
        </div>
      </Modal>
    );
  }

  renderTrigger() {
    return (
      <button
        className="workspace-action-btn workspace-action-btn-primary"
        onClick={action(() => {
          this.modalOpen = true;
        })}
      >
        <Icon name="plus" style={iconStyle} /> Create Study
      </button>
    );
  }

  renderCreateStudyForm() {
    const form = this.form;
    const projectIds = this.props.userStore.projectIdDropdown;
    const categoryId = form.$('categoryId').value;
    const isOrg = categoryId === 'organization';

    return (
      <Segment clearing className="p3 mb3">
        <Form form={form} onCancel={this.handleFormCancel} onSuccess={this.handleFormSubmission}>
          {({ processing, onCancel }) => (
            <>
              <Input field={form.$('id')} />
              <YesNo field={form.$('categoryId')} />
              <Input field={form.$('name')} />
              <TextArea field={form.$('description')} />
              <Dropdown field={form.$('projectId')} options={projectIds} fluid selection />
              {isOrg && (
                <div className="study-config-row mt2 mb2">
                  <div className="study-config-label">
                    <span className="study-config-label-text">License Study</span>
                    <span className="study-config-label-hint">Mark this as a license study. It will appear under the Licenses tab instead of Studies.</span>
                  </div>
                  <Checkbox
                    toggle
                    checked={!!form.$('isLicense').value}
                    onChange={() => form.$('isLicense').set(!form.$('isLicense').value)}
                  />
                </div>
              )}

              <div className="workspace-form-buttons">
                <button
                  className="workspace-action-btn workspace-action-btn-secondary"
                  disabled={processing}
                  onClick={onCancel}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="workspace-action-btn workspace-action-btn-primary"
                  disabled={processing}
                  type="submit"
                >
                  Create Study
                </button>
              </div>
            </>
          )}
        </Form>
      </Segment>
    );
  }
}

decorate(CreateStudy, {
  form: observable,
  modalOpen: observable,
  getStudiesStore: observable,
  isAdmin: computed,
  showAsResearcher: computed,
  cleanModal: action,
  handleFormSubmission: action,
});

export default inject(
  'userStore',
  'studiesStoresMap'
)(observer(CreateStudy));
